import { P2GBox } from '@parcel2go/core-ui';
import { P2GButtonGroup, P2GButtonGroupItem } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { Fragment, ReactNode, useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';

import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { Splitting } from '../grouping/Splitting';
import { Combining } from '../grouping/Combining';
import { Custom } from '../grouping/Custom';
import { useContext } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  AddNewItemsAction,
  SaveItemsAction,
  SaveTagsAction,
  SmartSendItemsAddedSocketAction,
  SmartSendItemsUpdatedSocketAction
} from '../../../lib/context/SmartSendContextModels';
import { SocketItemUpdateModel } from '../SmartSendSocketsModels';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { Convert } from '../grouping/Convert';
import { IsReturnCollection } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { uid } from '../../../lib/helpers/uid';
import { EventSections } from '../../../lib/events/EventLogger';
import { FilterToSendableItems } from './FilterToSendableItems';
import { GenerateGroupSummary } from './GenerateGroupSummary';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { Tagged } from '../../Tagged';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useCreateItemsHelper from '../../../lib/helpers/useItemCreateHelper';
import { P2GSidePanel } from '~/components/sidepanel';
import { P2GImageMessage } from '~/components/imagemessage';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { ContainsMarketplaceWithInternalSplitting } from '~/apps/smartsend/lib/helpers/ContainsMarketplaceWithInternalSplitting';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

export interface GroupingPanelProps {
  group: SmartSendItemGroup;
  onComplete?: () => void;
  control?: ReactNode;
}

export function ShowGroup(items: SmartSendItemApiModel[]): boolean {
  if (ContainsMarketplaceWithInternalSplitting(items)) {
    return false;
  }
  return (
    !items.find(IsReturnCollection) &&
    items[0].MarketplaceStatus === 'Sold' &&
    items.length == items.filter((x) => x.PropertyKey === items[0].PropertyKey).length
  );
}

type tabOption = 'GROUP' | 'SPLIT' | 'CUSTOM' | 'CONVERT' | '';

export const GroupingPanel: React.FC<GroupingPanelProps> = (props) => {
  const { createItemsHelper } = useCreateItemsHelper();
  const { createGroup } = useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<tabOption>('');
  const [options, setOptions] = useState<tabOption[]>([]);
  const [state, setState] = useState<'PROCESSING' | 'IDLE' | 'ERROR'>('IDLE');
  const { track } = useAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  useEffect(() => {
    if (tab) {
      track({
        type: 'smartsend_grouping_tab_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem, 'grouping']),
          tab: tab.toLowerCase()
        }
      });
    }
  }, [tab]);

  const key = useMemo(() => {
    let k = '';
    props.group.Items.forEach((i) => {
      k += `${i.Id}_`;
      i.MarketplaceOrderLines.filter(FilterToSendableItems).forEach((o) => {
        k += `${o.Id}_${o.Quantity}_`;
      });
    });
    return k;
  }, [props.group, props.group.Items.length]);

  useEffect(() => {
    const gs = GenerateGroupSummary(props.group.Items);

    if (gs.totalOrders == 0) {
      setOptions([]);
      return;
    }

    if (SettingsCache.settings.Features.includes('ParcelSplitting')) {
      if (gs.smartSendItemCount === 1 && gs.totalOrders == 1 && gs.totalQuantity > 1) {
        setOptions(['CONVERT']);
        return;
      }
      if (gs.totalOrders === 1 && gs.totalQuantity == 1) {
        setOptions(['SPLIT']);
        return;
      }
      if (gs.smartSendItemCount === 1 && gs.totalOrders > 1) {
        setOptions(['CUSTOM']);
        return;
      }
      if (gs.smartSendItemCount > 1 && gs.totalOrders > 2) {
        setOptions(['GROUP', 'CUSTOM']);
        return;
      }
    }

    if (gs.smartSendItemCount > 1) {
      setOptions(['GROUP']);
      return;
    }
  }, [key, props.group]);

  const title = useMemo(() => {
    if (options.length === 1) {
      switch (options[0]) {
        case 'GROUP':
          return t('GROUP_PARCELS_TOGETHER_TITLE', { defaultValue: 'Group Parcels Together' });
        case 'SPLIT':
        case 'CONVERT':
        case 'CUSTOM':
          return t('SEND_AS_MULTIPLE_SHIPMENTS_TITLE', { defaultValue: 'Send as Multiple Shipments' });
      }
    }

    if (options[0] === 'SPLIT' || options[0] === 'CONVERT') {
      return t('SPLIT_TO_NEW_SHIPMENTS_TITLE', { defaultValue: 'Split in to New Shipments' });
    }

    return t('GROUP_PARCELS_TITLE', { defaultValue: 'Group Parcels' });
  }, [options]);

  async function createItems(items: SmartSendItemApiModel[]) {
    setState('PROCESSING');
    const resp = await createGroup(items);

    if (resp.success) {
      const actions: SocketItemUpdateModel[] = [];

      // Deletes and Updates
      resp.result.DeletedItems.forEach((id) => {
        actions.push({ Id: id, ItemUpdates: { Status: 'Deleted' } });
      });
      resp.result.ModifiedItems.forEach((i) => {
        actions.push({ Id: i.Id, ItemUpdates: { MarketplaceOrderLines: i.MarketplaceOrderLines, Value: i.Value } });
      });
      ctx.dispatch(new SmartSendItemsUpdatedSocketAction({ Items: actions }));
      // New Items
      ctx.dispatch(new SmartSendItemsAddedSocketAction(resp.result.AddedItems, 0));

      setState('IDLE');
      setOpen(false);

      props.onComplete && props.onComplete();
    } else {
      setState('ERROR');
    }
  }

  async function createSplitItems(item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[], save: string[]) {
    setState('PROCESSING');
    let success = true;

    if (save.includes('Tags')) {
      ctx.dispatch(new SaveTagsAction([item.Id], item.Tags));
    }

    if (save.includes('MarketplaceOrderLines')) {
      ctx.dispatch(
        new SaveItemsAction([item.Id], ['MarketplaceOrderLines'], {
          MarketplaceOrderLines: item.MarketplaceOrderLines
        })
      );
    }

    await createItemsHelper(
      newItems.map((x) => {
        return { id: uid(), item: x, raw: [] };
      }),
      (items) => {
        ctx.dispatch(new AddNewItemsAction(items));
      },
      () => {
        success = false;
      },
      () => {}
    );

    if (success) {
      setState('IDLE');
      setOpen(false);

      props.onComplete && props.onComplete();
    } else {
      setState('ERROR');
    }
  }

  if (!props.group || options.length === 0) {
    return null;
  }

  return (
    <Fragment key={key}>
      {props.control ? (
        <P2GBox
          onClick={() => {
            setOpen(true);
            setTab(options[0]);
          }}
        >
          {props.control}
        </P2GBox>
      ) : (
        <P2GLink
          onClick={() => {
            setOpen(true);
            setTab(options[0]);
          }}
        >
          {title}
        </P2GLink>
      )}
      <P2GSidePanel anchor="right" open={open} maxWidth="md" onClose={() => setOpen(false)} title={title}>
        {open && (
          <Tagged tag="panel-grouping">
            <P2GBox textAlign="left">
              {state === 'IDLE' && (
                <P2GBox>
                  {options.length > 1 && (
                    <P2GBox>
                      <P2GButtonGroup fullWidth>
                        {options.includes('GROUP') && (
                          <P2GButtonGroupItem value="GROUP" onClick={(v) => setTab(v as tabOption)} selected={tab}>
                            {t('GROUP_TOGETHER_BUTTON', { defaultValue: 'Group together' })}
                          </P2GButtonGroupItem>
                        )}
                        {options.includes('SPLIT') && (
                          <P2GButtonGroupItem value="SPLIT" onClick={(v) => setTab(v as tabOption)} selected={tab}>
                            {t('SPLIT_BUTTON', { defaultValue: ' Split' })}
                          </P2GButtonGroupItem>
                        )}
                        {options.includes('CONVERT') && (
                          <P2GButtonGroupItem value="CONVERT" onClick={(v) => setTab(v as tabOption)} selected={tab}>
                            {t('CONVERT_BUTTON', { defaultValue: 'Convert' })}
                          </P2GButtonGroupItem>
                        )}
                        {options.includes('CUSTOM') && (
                          <P2GButtonGroupItem value="CUSTOM" onClick={(v) => setTab(v as tabOption)} selected={tab}>
                            {t('USE_MULTIPLE_SHIPMENTS_BUTTON', { defaultValue: 'Use multiple shipments' })}
                          </P2GButtonGroupItem>
                        )}
                      </P2GButtonGroup>
                    </P2GBox>
                  )}

                  <P2GBox pb={2} pt={2}>
                    {tab === 'GROUP' && <Combining group={props.group} create={createItems} getPackages={null} />}
                    {tab === 'SPLIT' && (
                      <Splitting
                        group={props.group}
                        split={(i, n) => createSplitItems(i, n, ['Tags', 'MarketplaceOrderLines'])}
                      />
                    )}
                    {tab === 'CONVERT' && (
                      <Convert
                        group={props.group}
                        split={(i, n) => createSplitItems(i, n, ['MarketplaceOrderLines'])}
                      />
                    )}
                    {tab === 'CUSTOM' && <Custom group={props.group} create={createItems} />}
                  </P2GBox>
                </P2GBox>
              )}
              {state === 'PROCESSING' && (
                <P2GImageMessage
                  image="/theme/smartsend/notifications/item-loading.png"
                  message={t('PROCESSING_FALLBACK', { defaultValue: 'Processing...' })}
                  contentKey="image-smartsend-grouping-loading"
                />
              )}
              {state === 'ERROR' && (
                <P2GImageMessage
                  image="/theme/smartsend/notifications/item-error.png"
                  message={t('THERE_WAS_ISSUE_ERROR_FALLBACK', {
                    defaultValue: 'There was an issue creating your grouped item...'
                  })}
                  contentKey="image-smartsend-grouping-error"
                />
              )}
            </P2GBox>
          </Tagged>
        )}
      </P2GSidePanel>
    </Fragment>
  );
};
